import { useQuery } from '@tanstack/react-query';
import apiClient from './apiClient';

const historicalActionKeys = {
  all: ['historicalActions'],
  list: (params) => [...historicalActionKeys.all, params],
  detail: (id) => [...historicalActionKeys.all, 'detail', id],
  modules: () => ['historicalActionModules'],
};

export const useHistoricalActionModules = () => {
  return useQuery({
    queryKey: historicalActionKeys.modules(),
    queryFn: async () => {
      const response = await apiClient.get('historical_actions/modules');
      if (response.data?.items) return response.data.items;
      return response.data || [];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useHistoricalActions = (params, options = {}) => {
  return useQuery({
    queryKey: historicalActionKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get('historical_actions', { params });
      return response.data;
    },
    keepPreviousData: true,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useHistoricalAction = (id, options = {}) => {
  return useQuery({
    queryKey: historicalActionKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`historical_actions/${id}`);
      return response.data;
    },
    enabled: Boolean(id),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Obtiene todas las acciones históricas para un filtro dado, paginando automáticamente.
 *
 * @param {Object} params - Filtros de búsqueda (mismos parámetros del endpoint).
 * @param {number} [batchSize=200] - Tamaño de página por request.
 * @returns {Promise<Array>} Lista completa de acciones.
 */
export const fetchAllHistoricalActions = async (params = {}, batchSize = 200) => {
  const normalizedBatchSize = Math.min(Math.max(Number(batchSize) || 200, 1), 200);
  const baseParams = { ...params };
  delete baseParams.page;
  delete baseParams.per_page;

  const items = [];
  let page = 1;
  let total = 0;

  do {
    const response = await apiClient.get('historical_actions', {
      params: {
        ...baseParams,
        page,
        per_page: normalizedBatchSize,
      },
    });

    const data = response?.data || {};
    const pageItems = Array.isArray(data.items) ? data.items : [];
    total = Number(data.total) || 0;
    items.push(...pageItems);
    page += 1;
  } while (items.length < total);

  return items;
};

/**
 * Obtiene una acción histórica con sus detalles por id.
 *
 * @param {number|string} id - Identificador de la acción histórica.
 * @returns {Promise<Object>} Acción histórica detallada.
 */
export const fetchHistoricalActionById = async (id) => {
  const response = await apiClient.get(`historical_actions/${id}`);
  return response?.data;
};
